import { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { PasseriniAPI } from '@/services/apiService';
import type { Tramite } from '@/types';

export interface TramiteSearchOption {
  id: string;
  label: string;
}

function buildLabel(tramite: Tramite): string {
  const parts: string[] = [];

  if (tramite.num_escritura) {
    parts.push(`#${tramite.num_escritura}`);
  }

  const vendedor = tramite.vendedores?.[0]?.nombre;
  const comprador = tramite.compradores?.[0]?.nombre;

  const personas = [vendedor, comprador].filter(Boolean).join(' / ');
  if (personas) {
    parts.push(personas);
  }

  if (parts.length === 0) {
    parts.push(`Trámite ${tramite.id}`);
  }

  return parts.join(' — ');
}

export const useTramitesSearch = (search: string) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isFetching } = useQuery({
    queryKey: ['tramites', 'search', debouncedSearch],
    queryFn: async () => {
      const response = await PasseriniAPI.get('/tramites', {
        params: { search: debouncedSearch, sort: 'created_at', order: 'desc' },
      });
      return (response.data.data as Tramite[]) || [];
    },
    enabled: debouncedSearch.length >= 2,
    staleTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });

  const opciones: TramiteSearchOption[] = (data || []).map((t) => ({
    id: String(t.id),
    label: buildLabel(t),
  }));

  const isPending = debouncedSearch !== search.trim();

  return { opciones, isLoading: isFetching || isPending };
};
